export const RETIREMENT_PLANNER_REDUCER_KEY = 'retirementPlannerStore';
export type RETIREMENT_PLANNER_REDUCER_KEY = typeof RETIREMENT_PLANNER_REDUCER_KEY;

import type { QcsResourceSlice } from 'app-components/flux/core/resourceSliceUtils';
import z from 'zod';

// Define enums or string literals using zod
export const RankSchema = z.enum(['BASIC', 'ADVANCED', 'BOTH', 'BASICHIDDEN']);
export type Rank = z.infer<typeof RankSchema>;

export const SectionsSchema = z.enum(['CURRENT', 'CONTRIBUTIONS', 'RETIREMENT', 'RETURN']);
export type Sections = z.infer<typeof SectionsSchema>;

// Define the RetirementFormData schema
export const RetirementFormDataSchema = z
  .object({
    startingAge: z.string(),
    lifeExpectancy: z.string(),
    retirementAge: z.string(),
    startingBalanceTaxDeferred: z.string(),
    startingBalanceTaxable: z.string(),
    startingBalanceCombined: z.string(),
    annualContributionTaxDeferred: z.string(),
    annualContributionTaxable: z.string(),
    annualContributionCombined: z.string(),
    annualContributionIncrease: z.string(),
    meanReturn: z.string(),
    meanReturnPostRetirement: z.string(),
    taxRate: z.string(),
    preRetirementTaxRate: z.string(),
    inflationRate: z.string(),
    annualWithdrawal: z.string(),
    retirementIncome: z.string(),
  })
  .partial();

export type RetirementFormData = z.infer<typeof RetirementFormDataSchema>;

// Define a separate schema for the 'data' object
export const RetirementDataSchema = z
  .object({
    formDataBasic: RetirementFormDataSchema,
    formDataAdvanced: RetirementFormDataSchema,
    isAdvanced: z.boolean(),
    showInflationAdjusted: z.boolean(),
    introVisible: z.boolean(),
  })
  .partial();

export type RetirementData = z.infer<typeof RetirementDataSchema>;

// Define RetirementStorageData schema
export const RetirementStorageDataSchema = z
  .object({
    id: z.string(),
    clientId: z.string(),
    createdAt: z.string(),
    modifiedAt: z.string(),
    isDeleted: z.boolean(),
    data: RetirementDataSchema,
  })
  .partial();

export type RetirementStorageData = z.infer<typeof RetirementStorageDataSchema>;

// Define FormField schema
const RetirementFormDataKeys = z.enum([
  'startingAge',
  'lifeExpectancy',
  'retirementAge',
  'startingBalanceTaxDeferred',
  'startingBalanceTaxable',
  'startingBalanceCombined',
  'annualContributionTaxDeferred',
  'annualContributionTaxable',
  'annualContributionCombined',
  'annualContributionIncrease',
  'meanReturn',
  'meanReturnPostRetirement',
  'taxRate',
  'preRetirementTaxRate',
  'inflationRate',
  'annualWithdrawal',
  'retirementIncome',
]);

export const FormFieldSchema = z.object({
  section: SectionsSchema,
  id: RetirementFormDataKeys,
  rank: RankSchema,
  default: z.string(),
});
export type FormField = z.infer<typeof FormFieldSchema>;

// Define the slice state type using QcsResourceSlice
export type RetirementPlannerState = QcsResourceSlice<RetirementStorageData>;
